import { useConvex } from "convex/react";
import { useEffect } from "react";
import { PanoramaPropertiesPanel } from "@/components/editor/PanoramaPropertiesPanel";
import { PropertiesPanel } from "@/components/editor/PropertiesPanel";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { updateHotspot as updateLocalHotspot, removeHotspot as removeLocalHotspot } from "@/data/adapters/local/hotspots";
import {
  updatePanorama as updateLocalPanorama,
  removePanorama as removeLocalPanorama,
  trashPanorama as trashLocalPanorama,
} from "@/data/adapters/local/panoramas";
import { createVersion as createLocalVersion } from "@/data/adapters/local/assetVersions";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { StorageService } from "@/services/storageService";
import { useEditorStore } from "@/store/editorStore";
import type { Panorama } from "@/store/editorStore";
import { useTextVariableFieldSync } from "@/hooks/useTextVariableFieldSync";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { computeFileHash } from "@/utils/fileHash";
import { api } from "../../convex/_generated/api";

type UpdatePayload = Record<string, unknown>;
type HotspotLike = { name?: string };

export const EditorPanel = () => {
  const convex = useConvex();
  const {
    selectedHotspot,
    updateHotspot,
    setHotspots,
    hotspots,
    selectedPanorama,
    setPanoramas,
    panoramas,
    setSelectedPanorama,
    setSelectedHotspot,
    showPanoramaProperties,
    setShowPanoramaProperties,
    skinComponents,
    project,
    duplicateHotspot,
    projectBackend,
  } = useEditorStore();

  const { user } = useAuth();
  const { toast } = useToast();
  useTextVariableFieldSync(project?.id, project?.craftspace_id);
  const isUuid = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value,
    );
  const useLocalBackend = isElectron() && projectBackend === "local";
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  const handleUpdateHotspot = async (hotspotId: string, updates: UpdatePayload) => {
    try {
      const existingHotspot = useEditorStore
        .getState()
        .hotspots.find((hotspot) => hotspot.id === hotspotId);
      const snapshotBefore = existingHotspot
        ? {
            id: existingHotspot.id,
            project_id: existingHotspot.project_id,
            panorama_id: existingHotspot.panorama_id,
            name: existingHotspot.name,
            type: existingHotspot.type,
            content: existingHotspot.content,
            position: existingHotspot.position,
            rotation: existingHotspot.rotation,
            scale: existingHotspot.scale,
            visible: existingHotspot.visible,
            order: existingHotspot.order,
            cms_action: (existingHotspot as { cms_action?: unknown }).cms_action,
            cmsAction: (existingHotspot as { cmsAction?: unknown }).cmsAction,
          }
        : null;

      // Update local store immediately for responsive UI
      updateHotspot(hotspotId, updates);

      // Save to database
      if (useLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, ...(updates as Parameters<typeof updateLocalHotspot>[0]) });
      } else {
        await convex.mutation(api.hotspots.update, { id: hotspotId, ...updates });
      }

      if (snapshotBefore) {
        const snapshotAfter = { ...snapshotBefore, ...updates };
        useEditorStore
          .getState()
          .recordHotspotUpdateWithSnapshots(hotspotId, snapshotBefore, snapshotAfter);
      }
    } catch (error) {
      console.error("Error updating hotspot:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update hotspot",
      });
    }
  };

  // Handler for deleting a hotspot - now deletes from database
  const handleDeleteHotspot = async (hotspotId: string) => {
    try {
      const hotspot = useEditorStore.getState().hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) {
        throw new Error("Hotspot not found");
      }

      // Delete from database first
      if (useLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await removeLocalHotspot(hotspotId, craftspaceId);
      } else {
        await convex.mutation(api.hotspots.remove, { id: hotspotId });
      }

      // Update local store after successful database deletion
      setHotspots(hotspots.filter((h) => h.id !== hotspotId));
      if (selectedHotspot?.id === hotspotId) {
        setSelectedHotspot(null);
      }
      useEditorStore.getState().recordHotspotDelete(hotspot);

    } catch (error) {
      console.error("Error deleting hotspot:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete hotspot",
      });
    }
  };

  // Handler for duplicating a hotspot
  const handleDuplicateHotspot = async (hotspot: HotspotLike) => {
    try {
      await duplicateHotspot(hotspot);
    } catch (error) {
      console.error("Error duplicating hotspot:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to duplicate hotspot",
      });
    }
  };

  useEffect(() => {
    if (selectedHotspot) {
      setShowPanoramaProperties(false);
    }
  }, [selectedHotspot, setShowPanoramaProperties]);

  // Handler for updating a panorama - now saves to database
  const handleUpdatePanorama = async (panoramaId: string, updates: UpdatePayload) => {
    try {
      const existingPanorama = useEditorStore
        .getState()
        .panoramas.find((panorama) => panorama.id === panoramaId);
      const snapshotBefore = existingPanorama
        ? {
            id: existingPanorama.id,
            project_id: existingPanorama.project_id,
            name: existingPanorama.name,
            order: existingPanorama.order,
            width: existingPanorama.width,
            height: existingPanorama.height,
            file_size: existingPanorama.file_size,
            file_url: existingPanorama.file_url,
            private_file_url: existingPanorama.private_file_url,
            thumbnail_url: existingPanorama.thumbnail_url,
            thumbnail_url_webp: existingPanorama.thumbnail_url_webp,
            file_status: existingPanorama.file_status,
            processing_status: existingPanorama.processing_status,
            gps_location: existingPanorama.gps_location,
            data: existingPanorama.data,
          }
        : null;

      // Update local store immediately for responsive UI
      // Use updatePanorama from store directly to avoid resetting selectedPanoramaIds (multi-select)
      useEditorStore.getState().updatePanorama(panoramaId, updates as Partial<Panorama>);

      // Save to database
      const shouldUseLocalBackend =
        isElectron() && (projectBackend === "local" || isUuid(panoramaId));
      if (shouldUseLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalPanorama({ id: panoramaId, craftspace_id: craftspaceId, ...updates });
      } else {
        await convex.mutation(api.panoramas.update, { id: panoramaId, ...updates });
      }

      if (snapshotBefore) {
        useEditorStore.getState().recordPanoramaUpdate(panoramaId, updates as Partial<Panorama>, {
          beforeOverride: snapshotBefore,
        });
      }
    } catch (error) {
      console.error("Error updating panorama:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update panorama",
      });
    }
  };

  // Handler for deleting a panorama - now deletes from database
  const handleDeletePanorama = async (panoramaId: string) => {
    try {
      // Delete from database first
      if (useLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await trashLocalPanorama(panoramaId, craftspaceId);
      } else {
        await convex.mutation(api.panoramas.remove, { id: panoramaId });
      }

      // Update local store after successful database deletion
      const remaining = panoramas.filter((p) => p.id !== panoramaId);
      setPanoramas(remaining);
      if (selectedPanorama?.id === panoramaId) {
        setSelectedPanorama(remaining[0] ?? null);
        setShowPanoramaProperties(false);
      }

      toast({
        title: "Panorama deleted",
        description: "Panorama has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting panorama:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete panorama",
      });
    }
  };

  // Handler for uploading a panorama
  const handleUploadPanorama = async (files: FileList, panoramaId?: string) => {
    if (!user || !project) return;

    try {
      const file = files[0]; // For replacing, we only handle one file

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast({
          variant: "destructive",
          title: "Invalid file type",
          description: `${file.name} is not an image file.`,
        });
        return;
      }

      const craftspaceId = project.craftspace_id || "default";
      const timestamp = Date.now();
      const filename = `${timestamp}_${file.name}`;

      // Show progress toast for upload
      const progressToast = toast({
        title: "Uploading panorama",
        description: `Uploading ${file.name}...`,
        progress: 0,
        duration: undefined, // Don't auto-dismiss
      });

      // Upload to private bucket first
      const panoramaName = file.name
        .replace(/\.[^/.]+$/, "")
        .replace(/[^a-zA-Z0-9-_]/g, "_")
        .toLowerCase();
      const privatePath = StoragePaths.private.panoramaOriginal(
        storageSegments.craftspaceKey,
        storageSegments.projectKey,
        panoramaName,
        filename,
      );

      // Update progress
      progressToast.update({
        title: "Uploading panorama",
        description: `Uploading ${file.name}...`,
        progress: 50,
      });

      const { data: _uploadData, error: uploadError } = await StorageService.uploadPrivate(
        "staging",
        privatePath,
        file,
      );

      if (uploadError) {
        console.error("Upload error:", uploadError);
        toast({
          variant: "destructive",
          title: "Upload failed",
          description: `Failed to upload ${file.name}: ${uploadError.message}`,
        });
        return;
      }

      // Get signed URL for private access
      const {
        data: { signedUrl },
      } = await StorageService.getPrivateUrl("staging", privatePath);

      // Extract image dimensions
      let imageDimensions = { width: null, height: null };
      try {
        const { getImageDimensions } = await import("@/utils/imageDimensions");
        const dimensions = await getImageDimensions(file);
        imageDimensions = { width: dimensions.width, height: dimensions.height };
        console.log(`📐 Extracted dimensions: ${dimensions.width}x${dimensions.height}`);
      } catch (error) {
        console.warn("Failed to extract image dimensions:", error);
      }

      // Update panorama record
      const updateData = {
        private_file_url: signedUrl,
        file_status: "private",
        file_size: file.size,
        width: imageDimensions.width,
        height: imageDimensions.height,
        processing_status: "pending" as const,
        data: {
          initialView: {
            position: { x: 0, y: 0, z: 0 },
            target: { x: 1, y: 0, z: 0 },
            fov: 75,
          },
        },
      };

      const panoramaData = await convex.mutation(api.panoramas.update, {
        id: panoramaId,
        ...updateData,
      });

      // Update store
      setPanoramas(panoramas.map((p) => (p.id === panoramaId ? panoramaData : p)));
      if (selectedPanorama?.id === panoramaId) {
        setSelectedPanorama(panoramaData);
      }

      // Process the image (generate thumbnails, WebP versions)
      try {
        console.log("🔄 Starting client-side panorama processing...");

        // Import client-side processing utilities
        const { compressPanorama } = await import("@/utils/clientPanoramaCompressor");
        const { generateClientThumbnails, uploadClientThumbnails } = await import(
          "@/utils/clientThumbnailGenerator"
        );

        // Step 1: Compress panorama for web use
        console.log("📸 Compressing panorama for web use...");
        const compressionResult = await compressPanorama(file, {
          quality: 0.85, // 85% quality
          format: "webp", // WebP for better compression
          targetFileSize: 5 * 1024 * 1024, // 5MB target
        });

        console.log(
          `✅ Compression complete: ${(compressionResult.compressed.size / 1024 / 1024).toFixed(2)}MB (${compressionResult.compressionRatio.toFixed(1)}x smaller)`,
        );

        // Step 2: Generate thumbnails
        console.log("🖼️ Generating thumbnails...");
        const thumbnails = await generateClientThumbnails(file);

        // Step 3: Upload compressed panorama and thumbnails
        console.log("📤 Uploading processed files...");

        // Upload compressed panorama
        const panoramaName = panoramaData.name;
        const compressedPath = StoragePaths.private.panoramaProcessed(
          storageSegments.craftspaceKey,
          storageSegments.projectKey,
          panoramaName,
          `compressed_${panoramaName}${compressionResult.extension}`,
        );
        await StorageService.uploadPrivate(
          "staging",
          compressedPath,
          compressionResult.compressed,
          { upsert: true },
        );

        // Get the URL for the compressed file to replace the original
        const {
          data: { signedUrl: compressedFileUrl },
        } = await StorageService.getPrivateUrl("staging", compressedPath);

        console.log("🔄 Replacing panorama with compressed version for better performance");

        // Upload thumbnails
        const thumbnailUrls = await uploadClientThumbnails(
          thumbnails,
          craftspaceId,
          project.id,
          panoramaName,
          storageSegments,
        );

        // Step 4: Update panorama record with processed URLs
        console.log("🔄 Updating panorama record with processed URLs...");
        await convex.mutation(api.panoramas.update, {
          id: panoramaData.id,
          private_file_url: compressedFileUrl, // Replace with compressed version
          thumbnail_url: thumbnailUrls.thumbnail_url,
          thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
          processing_status: "completed",
          file_status: "public", // Use 'public' instead of 'processed' to match constraint
          file_size: compressionResult.compressed.size, // Update file_size to compressed size
          // Store compression info
          data: {
            original_file_size: file.size,
            compressed_file_size: compressionResult.compressed.size,
            compression_ratio: compressionResult.compressionRatio,
            is_compressed: true,
            processing_method: "client-side",
          },
        });

        console.log("✅ Panorama processing completed successfully");

        // Update local store with processed data
        const updatedPanorama = {
          ...panoramaData,
          private_file_url: compressedFileUrl, // Replace with compressed version
          thumbnail_url: thumbnailUrls.thumbnail_url,
          thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
          processing_status: "completed",
          file_status: "public", // Use 'public' instead of 'processed' to match constraint
          file_size: compressionResult.compressed.size, // Update file_size to compressed size
          data: {
            ...panoramaData.data, // Preserve existing data including initialView
            original_file_size: file.size,
            compressed_file_size: compressionResult.compressed.size,
            compression_ratio: compressionResult.compressionRatio,
            is_compressed: true,
            processing_method: "client-side",
          },
        };

        setPanoramas(panoramas.map((p) => (p.id === panoramaId ? updatedPanorama : p)));
        if (selectedPanorama?.id === panoramaId) {
          setSelectedPanorama(updatedPanorama);
        }
      } catch (processingError) {
        console.error("Client-side processing error:", processingError);
        // Don't fail the upload if processing fails - panorama is still usable
        toast({
          variant: "destructive",
          title: "Processing warning",
          description:
            "Panorama uploaded successfully but processing failed. It may not have thumbnails.",
        });
      }

      // Create asset version record for this upload
      try {
        const fileHash = await computeFileHash(file);
        // Get the latest panorama state from the store
        const currentPan = useEditorStore.getState().panoramas.find((p) => p.id === panoramaId);

        if (useLocalBackend) {
          await createLocalVersion({
            project_id: project.id,
            entity_type: "panorama",
            entity_id: panoramaId!,
            file_url: currentPan?.file_url ?? signedUrl,
            private_file_url: currentPan?.private_file_url ?? signedUrl,
            file_hash: fileHash,
            file_size: currentPan?.file_size ?? file.size,
            width: currentPan?.width ?? imageDimensions.width ?? undefined,
            height: currentPan?.height ?? imageDimensions.height ?? undefined,
            original_filename: file.name,
            metadata: { processing_method: "client-side" },
          });
        } else {
          await convex.mutation(api.assetVersions.create, {
            project_id: project.id as any,
            entity_type: "panorama",
            entity_id: panoramaId!,
            file_url: currentPan?.file_url ?? signedUrl,
            private_file_url: currentPan?.private_file_url ?? signedUrl,
            file_hash: fileHash,
            file_size: currentPan?.file_size ?? file.size,
            width: currentPan?.width ?? imageDimensions.width ?? undefined,
            height: currentPan?.height ?? imageDimensions.height ?? undefined,
            original_filename: file.name,
            metadata: { processing_method: "client-side" },
          });
        }
      } catch (versionError) {
        console.warn("Failed to create asset version:", versionError);
        // Non-critical — don't fail the upload
      }

      // Update progress toast to show completion
      progressToast.update({
        title: "Upload complete",
        description: `${file.name} uploaded successfully`,
        progress: 100,
      });

      // Dismiss progress toast after a short delay
      setTimeout(() => {
        progressToast.dismiss();
      }, 2000);
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "An unexpected error occurred during upload.",
      });
    }
  };

  return (
    <div className="h-full bg-gray-800">
      {selectedHotspot ? (
        <PropertiesPanel
          updateHotspotImpl={handleUpdateHotspot}
          deleteHotspotImpl={handleDeleteHotspot}
          duplicateHotspotImpl={handleDuplicateHotspot}
          skinComponents={skinComponents}
        />
      ) : showPanoramaProperties ? (
        <PanoramaPropertiesPanel
          updatePanoramaImpl={handleUpdatePanorama}
          deletePanoramaImpl={handleDeletePanorama}
          uploadPanoramaImpl={handleUploadPanorama}
        />
      ) : null}
    </div>
  );
};

export default function Editor() {
  return null;
}
